import { useCallback, useEffect, useMemo, useState } from 'react';
import { BulletinBoardScene } from './BulletinBoardScene.jsx';

const defaults = Object.freeze({
  corner_store_smokes_price: 8,
  corner_store_beer_price: 4,
  corner_store_scratcher_price: 3,
  corner_store_smokes_daily_limit: 10,
  corner_store_beer_daily_limit: 12,
  corner_store_scratcher_daily_limit: 20,
});

function money(value) {
  return `$${Number(value || 0).toLocaleString()}`;
}

function Choice({ number, label, detail, disabled, onClick, tone = '' }) {
  return (
    <button className={`corner-store-choice ${tone}`} disabled={disabled} onClick={onClick}>
      <span>{number}.</span>
      <span><strong>{label}</strong>{detail && <small>{detail}</small>}</span>
    </button>
  );
}

function QuantityPicker({ label, value, onChange, maximum, disabled }) {
  const max = Math.max(1, Number(maximum || 1));
  const set = (next) => onChange(Math.max(1, Math.min(max, Number.parseInt(next || 1, 10) || 1)));
  return (
    <div className="corner-store-quantity">
      <span>{label}</span>
      <button type="button" disabled={disabled || value <= 1} onClick={() => set(value - 1)}>−</button>
      <input aria-label={`${label} quantity`} type="number" min="1" max={max} value={value} disabled={disabled} onChange={(event) => set(event.target.value)} />
      <button type="button" disabled={disabled || value >= max} onClick={() => set(value + 1)}>+</button>
      <button type="button" className="quantity-max" disabled={disabled} onClick={() => set(max)}>MAX</button>
    </div>
  );
}

export function CornerStoreScene({ gameState, busy, actions }) {
  const [screen, setScreen] = useState('main');
  const [smokesQuantity, setSmokesQuantity] = useState(1);
  const [beerQuantity, setBeerQuantity] = useState(1);
  const [scratcherQuantity, setScratcherQuantity] = useState(1);
  useEffect(() => setScreen('main'), [gameState.currentLocationId]);

  const rules = { ...defaults, ...(gameState.serverRules || {}) };
  const runtime = gameState.cornerStore || {};
  const inventory = gameState.streetInventory || { smokes: 0, beer: 0 };
  const smokesRemaining = Math.max(0, Number(rules.corner_store_smokes_daily_limit) - Number(runtime.smokesBought || 0));
  const beerRemaining = Math.max(0, Number(rules.corner_store_beer_daily_limit) - Number(runtime.beerBought || 0));
  const scratchersRemaining = Math.max(0, Number(rules.corner_store_scratcher_daily_limit) - Number(runtime.scratchersBought || 0));
  const results = Array.isArray(runtime.lastScratcherResults) ? runtime.lastScratcherResults : [];
  const reveals = Array.isArray(runtime.lastScratcherReveals) ? runtime.lastScratcherReveals : [];
  const board = gameState.bulletinBoard || {};

  useEffect(() => setSmokesQuantity((value) => Math.max(1, Math.min(value, Math.max(1, smokesRemaining)))), [smokesRemaining]);
  useEffect(() => setBeerQuantity((value) => Math.max(1, Math.min(value, Math.max(1, beerRemaining)))), [beerRemaining]);
  useEffect(() => setScratcherQuantity((value) => Math.max(1, Math.min(value, Math.max(1, scratchersRemaining)))), [scratchersRemaining]);

  const limits = useMemo(() => [
    ['SMOKES', smokesRemaining, rules.corner_store_smokes_daily_limit],
    ['BEER', beerRemaining, rules.corner_store_beer_daily_limit],
    ['SCRATCHERS', scratchersRemaining, rules.corner_store_scratcher_daily_limit],
  ], [smokesRemaining, beerRemaining, scratchersRemaining, rules]);

  const buyDisabled = (price, quantity, remaining) => busy || quantity > remaining || Number(gameState.cash) < Number(price) * quantity;
  const backFromBoard = useCallback(() => setScreen('main'), []);
  const openBoard = async () => {
    const accepted = await actions.openCornerStoreBoard();
    if (!accepted) return;
    if (!actions.openV2Window?.('bulletin')) setScreen('board');
  };

  let title = 'The Corner Store';
  let copy = <>Everyone in the neighborhood ends up here sooner or later.<br />Cigs, beer, gas, scratchers, gossip, and bad decisions all under one leaky roof.</>;
  let choices = (
    <>
      <div className="corner-store-limit-strip">{limits.map(([label, remaining, maximum]) => <span key={label}><b>{label}</b>{remaining}/{maximum}</span>)}</div>
      <Choice number="1" label="Buy smokes" detail={`${money(rules.corner_store_smokes_price)} a pack · ${smokesRemaining} left today`} disabled={busy} onClick={() => setScreen('smokes')} tone="lime" />
      <Choice number="2" label="Get beer and scratchers" detail="Cold beer, warm regrets, and silver dust under your fingernails" disabled={busy} onClick={() => setScreen('counter')} />
      <Choice number="3" label="Check the bulletin board" detail="System notices now; jobs, graffiti, rumors, and player requests later" disabled={busy} onClick={openBoard} />
      <Choice number="4" label="Leave before something weird happens" disabled={busy} onClick={actions.leaveCornerStore} />
    </>
  );

  if (screen === 'smokes') {
    title = 'Smokes Behind the Counter';
    copy = <>The cashier points at a sun-faded rack and watches you like you already stole something.<br />You currently have <strong>{Number(inventory.smokes || 0)}</strong> pack{Number(inventory.smokes || 0) === 1 ? '' : 's'}.</>;
    choices = (
      <>
        <QuantityPicker label="PACKS" value={smokesQuantity} onChange={setSmokesQuantity} maximum={smokesRemaining} disabled={busy || smokesRemaining <= 0} />
        <Choice number="1" label={`Buy ${smokesQuantity} pack${smokesQuantity === 1 ? '' : 's'} · ${money(Number(rules.corner_store_smokes_price) * smokesQuantity)}`} detail={`${smokesRemaining} remaining today`} disabled={buyDisabled(rules.corner_store_smokes_price, smokesQuantity, smokesRemaining)} onClick={() => actions.buySmokes(smokesQuantity)} tone="lime" />
        <Choice number="2" label="Back to the store" disabled={busy} onClick={() => setScreen('main')} />
      </>
    );
  } else if (screen === 'counter') {
    title = 'Beer and Scratchers';
    copy = <>The beer is cold. The scratchers promise riches in a font normally used for eviction notices.<br />Beer on you: <strong>{Number(inventory.beer || 0)}</strong>. Scratchers left today: <strong>{scratchersRemaining}</strong>.</>;
    choices = (
      <>
        <QuantityPicker label="BEER" value={beerQuantity} onChange={setBeerQuantity} maximum={beerRemaining} disabled={busy || beerRemaining <= 0} />
        <Choice number="1" label={`Buy ${beerQuantity} beer${beerQuantity === 1 ? '' : 's'} · ${money(Number(rules.corner_store_beer_price) * beerQuantity)}`} detail={`${beerRemaining} remaining today`} disabled={buyDisabled(rules.corner_store_beer_price, beerQuantity, beerRemaining)} onClick={() => actions.buyBeer(beerQuantity)} tone="lime" />
        <Choice number="2" label="Drink one outside" detail="A mild server-rolled effect; occasionally the cooler betrays you" disabled={busy || Number(inventory.beer || 0) <= 0} onClick={actions.drinkBeer} />
        <QuantityPicker label="SCRATCHERS" value={scratcherQuantity} onChange={setScratcherQuantity} maximum={scratchersRemaining} disabled={busy || scratchersRemaining <= 0} />
        <Choice number="3" label={`Buy ${scratcherQuantity} scratcher${scratcherQuantity === 1 ? '' : 's'} · ${money(Number(rules.corner_store_scratcher_price) * scratcherQuantity)}`} detail="Each ticket has exactly a 50% chance of paying something" disabled={buyDisabled(rules.corner_store_scratcher_price, scratcherQuantity, scratchersRemaining)} onClick={() => actions.buyScratchers(scratcherQuantity)} />
        <Choice number="4" label="Back to the store" disabled={busy} onClick={() => setScreen('main')} />
      </>
    );
  }

  if (screen === 'board') return <BulletinBoardScene board={board} busy={busy} onBack={backFromBoard} />;

  return (
    <div className={`corner-store-overlay ${gameState.activeEvent ? 'event-behind' : ''}`} role="dialog" aria-modal="true" aria-labelledby="corner-store-title">
      <div className="corner-store-frame">
        <div className="corner-store-artwork">
          <img src="./scenes/corner-store.png" alt="The grimy Corner Store at night" />
          <div className="corner-store-vignette" />
          <div className="corner-store-label">LOCAL STOP · NO DAY USED</div>
          <div className="corner-store-wallet">CASH <strong>{money(gameState.cash)}</strong></div>
        </div>
        <div className="corner-store-lower">
          <section className="corner-store-dialogue">
            <span className="scene-kicker">TRAILER PARK COMMERCE</span>
            <h2 id="corner-store-title">{title}</h2>
            <p>{copy}</p>
            {screen === 'counter' && results.length > 0 && (
              <div className={`scratcher-result ${Number(runtime.lastScratcherPayout || 0) > 0 ? 'winner' : 'loser'}`}>
                <span>LAST SCRATCH</span>
                <div>{results.map((result, index) => <strong key={`${index}-${result}`}>{result ? money(result) : 'LOSER'}</strong>)}</div>
                {reveals.map((reveal, index) => <small key={`${index}-${reveal}`}>{index + 1}. {reveal}</small>)}
                <p>Spent {money(runtime.lastScratcherCost)} · Paid {money(runtime.lastScratcherPayout)}</p>
              </div>
            )}
          </section>
          <section className="corner-store-choices" aria-label="Corner Store choices">{choices}</section>
        </div>
        {busy && <div className="corner-store-busy">THE CASHIER IS COUNTING SOMETHING…</div>}
      </div>
    </div>
  );
}
